import { Collapsible } from '@base-ui/react/collapsible';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';

import { useHealthCenter } from '@/api/healthCenter';
import AIIcon from '@/assets/svg/ai-stars.svg?react';
import AreaIcon from '@/assets/svg/bx-area.svg?react';
import BarChartIcon from '@/assets/svg/bx-bar-chart-alt.svg?react';
import BuildingIcon from '@/assets/svg/bx-buildings-alt.svg?react';
import CodeIcon from '@/assets/svg/bx-code-alt.svg?react';
import DollarCircleIcon from '@/assets/svg/bx-dollar-circle.svg?react';
import LinkIcon from '@/assets/svg/bx-link.svg?react';
import MapPinIcon from '@/assets/svg/bx-map-pin.svg?react';
import TrendingUpIcon from '@/assets/svg/bx-trending-up.svg?react';
import UserAddIcon from '@/assets/svg/bx-user-plus.svg?react';
import BulletSeparated from '@/components/BulletSeparated';
import { LinkButton } from '@/components/buttons';
import { SkeletonRectangle } from '@/components/loaders';
import { Page } from '@/components/page';
import { Panel } from '@/components/panels';
import { Text, Title } from '@/components/typography';
import { FEATURE_FLAG } from '@/constants/featureFlags';
import { formatDate, formatDateFromNow } from '@/helper/dateHelper';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useMe } from '@/providers/User';
import { ROUTES } from '@/router';

import Fireworks from './ConfettiCanvas';
import HealthPod from './HealthPod';
import HealthPodDetail from './HealthPodDetails';
import HealthPodUser from './HealthPodUser';
import OnboardingProgressBar from './OnboardingProgressBar';
import SetupWelcome from './SetupWelcome';

const HealthCenter = () => {
  const { healthCenterData, isLoading } = useHealthCenter();
  const user = useMe();
  const hasAIVisibility = !!useFeatureFlag(FEATURE_FLAG.aiVisibility);
  const tasks = [
    {
      id: 'aiVisibility',
      data: healthCenterData?.aiVisibility && {
        ...healthCenterData.aiVisibility,
        isAvailable: hasAIVisibility,
      },
      icon: AIIcon,
      title: t`AI Visibility`,
      instructions: t`Configure your brand and product areas, add competitors, and start your first analysis`,
      link:
        user?.isAdmin || user?.isC99Superuser
          ? ROUTES.aiVisibilitySettings.path
          : ROUTES.aiVisibilityOverview.path,
      details: (
        <HealthPodDetail label={t`First Analysis Started`}>
          {formatDate(healthCenterData?.aiVisibility?.firstRunAt, 'MM/DD/YYYY')}
        </HealthPodDetail>
      ),
    },
    {
      id: 'tag',
      data: healthCenterData?.tag,
      icon: CodeIcon,
      title: t`Channel99 Tag`,
      titleInfoPopover: {
        title: t`Channel99 Tag`,
        body: t`Channel99 Tag will indicate "Incomplete" if we do not receive a pageview within 24 hours.`,
      },
      instructions: t`Place Channel99 Tag on your website`,
      link: ROUTES.companySettings.path,
      details: (
        <BulletSeparated>
          <HealthPodDetail label={t`Installed`}>
            {formatDate(healthCenterData?.tag?.tagInstalledAt, 'MM/DD/YYYY')}
          </HealthPodDetail>
          <HealthPodDetail label={t`Last Data Received`}>
            {formatDate(healthCenterData?.tag?.lastEventAt, 'MM/DD/YYYY')}
          </HealthPodDetail>
        </BulletSeparated>
      ),
    },
    {
      id: 'tam',
      data: healthCenterData?.tam,
      icon: MapPinIcon,
      title: t`Total Addressable Market`,
      instructions: t`Define your organization's TAM (Total Addressable Market)`,
      link: ROUTES.companySettings.path,
      details: (
        <BulletSeparated>
          <HealthPodDetail label={t`Last Updated`}>
            {formatDate(healthCenterData?.tam?.updatedAt, 'MM/DD/YYYY')}
          </HealthPodDetail>
          <HealthPodDetail label={t`By`}>
            <HealthPodUser user={healthCenterData?.tam?.updatedBy} />
          </HealthPodDetail>
        </BulletSeparated>
      ),
    },
    {
      id: 'adVendors',
      data: healthCenterData?.adVendors,
      title: t`Ad Vendors`,
      icon: LinkIcon,
      instructions: t`Automated Spend and Metrics from Ad Vendors`,
      link: ROUTES.integrationByCategory('advertising'),
      details: (
        <BulletSeparated>
          <HealthPodDetail label={t`Last Created`}>
            {formatDate(healthCenterData?.adVendors?.createdAt, 'MM/DD/YYYY')}
          </HealthPodDetail>
          <HealthPodDetail label={t`By`}>
            <HealthPodUser user={healthCenterData?.adVendors?.createdBy} />
          </HealthPodDetail>
        </BulletSeparated>
      ),
    },
    {
      id: 'crm',
      data: healthCenterData?.crm,
      icon: BarChartIcon,
      title: t`CRM`,
      instructions: t`Connect your CRM for pipeline data and audience creation`,
      link: ROUTES.integrationByCategory('crm'),
      details: (
        <BulletSeparated>
          <HealthPodDetail label={t`Last Sync`}>
            {formatDateFromNow(healthCenterData?.crm?.lastSynced)}
          </HealthPodDetail>
        </BulletSeparated>
      ),
    },
    {
      id: 'customAudience',
      data: healthCenterData?.customAudience,
      icon: BuildingIcon,
      title: t`Custom Audience`,
      instructions: t`Create Custom Audience for more precise insights`,
      link: ROUTES.audiences.path,
      details: (
        <BulletSeparated>
          <HealthPodDetail label={t`Last Created`}>
            {formatDate(healthCenterData?.customAudience?.createdAt, 'MM/DD/YYYY')}
          </HealthPodDetail>
          <HealthPodDetail label={t`By`}>
            <HealthPodUser user={healthCenterData?.customAudience?.createdBy} />
          </HealthPodDetail>
        </BulletSeparated>
      ),
    },
    {
      id: 'userAdmin',
      data: healthCenterData?.userAdmin,
      icon: UserAddIcon,
      title: t`Users and Admins`,
      instructions: t`Invite Users and Admins to the C99 Platform`,
      link: ROUTES.users.path,
      details: (
        <BulletSeparated>
          <HealthPodDetail label={t`Last User Added`}>
            {formatDate(healthCenterData?.userAdmin?.lastUserAddedAt, 'MM/DD/YYYY')}
          </HealthPodDetail>
        </BulletSeparated>
      ),
    },
    {
      id: 'universalPixel',
      data: healthCenterData?.universalPixel,
      icon: AreaIcon,
      title: t`C99 Universal Pixel`,
      instructions: t`Publish and place your C99 Universal Pixel`,
      link: ROUTES.pixels.path,
      details: (
        <BulletSeparated>
          <HealthPodDetail label={t`Created On`}>
            {formatDate(healthCenterData?.universalPixel?.lastCreatedAt, 'MM/DD/YYYY')}
          </HealthPodDetail>
          <HealthPodDetail label={t`By`}>
            <HealthPodUser user={healthCenterData?.universalPixel?.createdBy} />
          </HealthPodDetail>
        </BulletSeparated>
      ),
    },
    {
      id: 'channelMapping',
      data: healthCenterData?.channelMapping,
      icon: TrendingUpIcon,
      title: t`Channel Mapping`,
      instructions: t`Review Channel Mapping`,
      link: ROUTES.configureChannels.path,
      details: (
        <BulletSeparated>
          <HealthPodDetail label={t`Last Update`}>
            {formatDate(healthCenterData?.channelMapping?.updatedAt, 'MM/DD/YYYY')}
          </HealthPodDetail>
          <HealthPodDetail label={t`By`}>
            <HealthPodUser user={healthCenterData?.channelMapping?.updatedBy} />
          </HealthPodDetail>
        </BulletSeparated>
      ),
    },
    {
      id: 'vendorSpend',
      data: healthCenterData?.vendorSpend,
      icon: DollarCircleIcon,
      title: t`Vendor Spend`,
      instructions: t`Keep Vendor Spend up to date`,
      link: ROUTES.configureVendors.path,
      details: (
        <BulletSeparated>
          <HealthPodDetail label={t`Spend Input Through`}>
            {formatDate(healthCenterData?.vendorSpend?.spendInputThroughAt, 'MM/YYYY')}
          </HealthPodDetail>
          {healthCenterData?.vendorSpend?.lastUpdatedBy != null && (
            <HealthPodDetail label={t`By`}>
              <HealthPodUser user={healthCenterData?.vendorSpend?.lastUpdatedBy} />
            </HealthPodDetail>
          )}
        </BulletSeparated>
      ),
    },
  ];

  const eligibleTasks = tasks.filter(
    ({ data }) => data?.isAvailable && (user?.isPaid || data.isFree),
  );
  const incompleteTasks = eligibleTasks.filter(({ data }) => !data?.isComplete);
  const prioritizedTasks = incompleteTasks.filter(
    ({ id }) => id !== 'channelMapping' && id !== 'vendorSpend',
  );
  const otherTasks = incompleteTasks.filter(
    ({ id }) => id === 'channelMapping' || id === 'vendorSpend',
  );
  const completedTasks = eligibleTasks.filter(({ data }) => data?.isComplete);
  const unavailableTasks = tasks.filter((task) => !eligibleTasks.includes(task));
  const nextTask = isLoading ? undefined : prioritizedTasks[0];
  const renderTask = ({ id, details, ...props }: (typeof tasks)[number]) => (
    <li key={id}>
      <HealthPod
        {...props}
        isLoading={isLoading}
        isNext={id === nextTask?.id}
        priority={prioritizedTasks.findIndex((task) => task.id === id) + 1 || undefined}
      >
        {details}
      </HealthPod>
    </li>
  );

  return (
    <>
      <Page
        title={t`Health Center`}
        className="@container flex flex-1 flex-col gap-6"
        exampleAIQuestions={[]}
      >
        <Panel className="@container overflow-hidden" noPadding>
          <div className="grid @3xl:grid-cols-2">
            <section
              aria-label={t`Setup Progress`}
              className="flex min-h-52 items-center gap-5 p-6"
            >
              <OnboardingProgressBar
                isLoading={isLoading && !healthCenterData?.onboardingProgress}
                value={healthCenterData?.onboardingProgress}
              />
              <div className="min-w-0">
                <Title level={3} weight="bold">
                  <Trans>Setup Progress</Trans>
                </Title>
                <Text variant="body2" color="dark-grey" className="mt-1 block">
                  <Trans>Onboarding completion</Trans>
                </Text>
              </div>
            </section>
            <section
              aria-label={t`What's next`}
              className="m-3 flex flex-col justify-center gap-3 rounded-xl border-2 border-green-400 bg-green-100/30 p-6"
            >
              <Title level={3} weight="bold">
                <Trans>What's next</Trans>
              </Title>
              {isLoading && <SkeletonRectangle width="100%" height={48} />}
              {!isLoading && !!nextTask && (
                <div className="flex flex-wrap items-center gap-4">
                  <Text
                    variant="body1"
                    weight="bold"
                    className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary"
                    aria-hidden="true"
                  >
                    1
                  </Text>
                  <div className="min-w-0 flex-1">
                    <Text variant="body1" weight="bold" className="block">
                      {nextTask.title}
                    </Text>
                    <Text variant="body2" color="dark-grey" className="mt-1 block">
                      {nextTask.instructions}
                    </Text>
                  </div>
                  <LinkButton to={nextTask.link} variant="primary">
                    <Trans>Start setup</Trans>
                  </LinkButton>
                </div>
              )}
              {!isLoading && !nextTask && !!healthCenterData && (
                <Text variant="body2" color="dark-grey">
                  <Trans>No prioritized setup task is currently available.</Trans>
                </Text>
              )}
            </section>
          </div>
        </Panel>
        {isLoading ? (
          <ul
            className="grid grid-cols-1 gap-x-8 gap-y-3 @4xl:grid-cols-2"
            aria-label={t`Setup loading`}
          >
            {tasks.map(renderTask)}
          </ul>
        ) : (
          healthCenterData && (
            <>
              {prioritizedTasks.length > 0 && (
                <section aria-label={t`Remaining setup`} className="flex flex-col gap-4">
                  <Title level={2} weight="bold">
                    <Trans>Remaining setup</Trans>
                  </Title>
                  <ol
                    role="list"
                    aria-label={t`Prioritized setup`}
                    className="grid list-none! grid-cols-1 gap-x-8 gap-y-3 @4xl:grid-cols-2"
                  >
                    {prioritizedTasks.map(renderTask)}
                  </ol>
                </section>
              )}
              {eligibleTasks.length > 0 && incompleteTasks.length === 0 && (
                <Text
                  variant="body2"
                  color="dark-green"
                  className="rounded-xl border border-green-300 bg-green-100 p-5"
                >
                  <Trans>All available setup tasks are complete.</Trans>
                </Text>
              )}
              {eligibleTasks.length === 0 && (
                <Text
                  variant="body2"
                  color="dark-grey"
                  className="rounded-xl border border-gray-300 bg-white p-5"
                >
                  <Trans>No setup tasks are currently available for your plan.</Trans>
                </Text>
              )}
              {otherTasks.length > 0 && (
                <section aria-label={t`Other setup tasks`} className="flex flex-col gap-4">
                  <Title level={2} weight="bold">
                    <Trans>Other setup tasks</Trans>
                  </Title>
                  <ul className="grid grid-cols-1 gap-x-8 gap-y-3 @4xl:grid-cols-2">
                    {otherTasks.map(renderTask)}
                  </ul>
                </section>
              )}
              {completedTasks.length > 0 && (
                <Collapsible.Root
                  defaultOpen
                  className="rounded-2xl border-2 border-gray-300 bg-white"
                >
                  <Title level={2} weight="bold">
                    <Collapsible.Trigger className="group flex w-full items-center justify-between rounded-xl p-5 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500">
                      <span>
                        <Trans>Completed setup ({completedTasks.length})</Trans>
                      </span>
                      <span aria-hidden="true" className="group-data-panel-open:rotate-180">
                        ⌄
                      </span>
                    </Collapsible.Trigger>
                  </Title>
                  <Collapsible.Panel>
                    <ul className="grid grid-cols-1 gap-x-8 gap-y-3 px-5 pb-5 @4xl:grid-cols-2">
                      {completedTasks.map(renderTask)}
                    </ul>
                  </Collapsible.Panel>
                </Collapsible.Root>
              )}
              {unavailableTasks.length > 0 && (
                <section aria-label={t`Unavailable setup`} className="flex flex-col gap-4">
                  <Title level={2} weight="bold">
                    <Trans>Unavailable setup</Trans>
                  </Title>
                  <ul className="grid grid-cols-1 gap-x-8 gap-y-3 @4xl:grid-cols-2">
                    {unavailableTasks.map(renderTask)}
                  </ul>
                </section>
              )}
            </>
          )
        )}
      </Page>
      <SetupWelcome
        key={user?.currentOrg.instanceId}
        healthCenterData={healthCenterData}
        isLoading={isLoading}
      />
      {healthCenterData?.onboardingProgress === 1 && <Fireworks />}
    </>
  );
};

export default HealthCenter;
